import { DebriefingMin } from 'pages/debriefing/types';
import { MatterAssignmentMinDto } from './assignments';
import { PartyMinDtoWithRelationships } from './contacts';
import { CorrespondenceListElementMinDto } from './correspondence';
import { ClientParty, MatterListItemMin, MatterMin, MatterMinWithClientParty, MatterVisibilityInfo } from './matters';
import { MinDto, MinStringOption, NamedEntityMinDto, NumericIdPrimKeyDto } from './other';

export enum TaskTypeCode {
    CORRESPONDENCE = 'CORRESPONDENCE',
    CASE_DEADLINE = 'CASE_DEADLINE',
    COURT_HEARING = 'COURT_HEARING',
    FILING_DEADLINE = 'FILING_DEADLINE',
    PROCEDURAL_DEADLINE = 'PROCEDURAL_DEADLINE',
    COMPLIANCE_TASK = 'COMPLIANCE_TASK',
}

export enum TaskPriorityCode {
    MEDIUM = 'MEDIUM',
    HIGH = 'HIGH',
}

export enum TaskStatusType {
    DONE = 'DONE',
    IN_PROGRESS = 'IN_PROGRESS',
}

export interface TaskListItem {
    id: number;
    name: string;
    clientParties: PartyMinDtoWithRelationships[];
    matters: MatterMinWithClientParty[];
    assignedByEmployees: NamedEntityMinDto[];
    assignedToEmployees: NamedEntityMinDto[];
    deadlineDate?: string;
    calendarEventDisplay?: string;
    calendarEventStartDate?: string;
    isAllDayEvent?: boolean;
    taskPriorityTypeCode: string;
    taskStatusTypeCode: `${TaskStatusType}`;
    taskTypeCodes: string[];
    isOfficial: boolean;
    officialReference: string | null;
    isAutomatic?: boolean;
    createdAt?: string;
    /** A draft may also lack its name and priority. */
    isDraft?: boolean;
}

export interface AddressedTaskListItem {
    id: number;
    name: string;
    clientParties: PartyMinDtoWithRelationships[];
    matters: MatterMin[];
    correspondence?: CorrespondenceListElementMinDto[];
    taskPriorityTypeCode: string;
    taskStatusTypeCode: string;
    taskTypeCodes: string[];
    isAutomatic?: boolean;
}

export interface Task {
    id: number;
    name: string;
    description?: string;
    taskPriorityTypeCode: string;
    taskStatusTypeCode: string;
    taskTypeCodes: string[];
    assignedToEmployees: NamedEntityMinDto[];
    assignedByEmployees: NamedEntityMinDto[];
    clientParties: PartyMinDtoWithRelationships[];
    matters: MatterListItemMin[];
    assignments?: MatterAssignmentMinDto[];
    correspondence: CorrespondenceListElementMinDto[];
    debriefings?: DebriefingMin[];
    hasDeadline: boolean;
    deadlineDate?: string;
    isOfficial: boolean;
    officialReference: string;
    notes: string;
    taskEvent?: TaskEventDto;
    dmsData?: DmsDataDto;
    createdAt: number;
    lastModifiedAt: number;
    createdByEmployee?: MinDto;
    lastModifiedByEmployee?: MinDto;
    isAutomatic?: boolean;
    /** A draft may also lack its name, priority and event dates. */
    isDraft?: boolean;
    organizerEmail?: string;
}

export interface TaskDto {
    id?: string | number | null;
    name?: string;
    description?: string;
    taskStatusTypeCode?: string;
    taskPriorityTypeCode?: string;
    clientParties?: NumericIdPrimKeyDto[];
    taskTypeCodes?: string[];
    assignedToEmployees?: NumericIdPrimKeyDto[];
    assignedByEmployees?: NumericIdPrimKeyDto[];
    matters?: NumericIdPrimKeyDto[];
    assignments?: NumericIdPrimKeyDto[];
    correspondence: NumericIdPrimKeyDto[];
    /** null/undefined leaves existing links untouched; [] clears them all. */
    debriefings?: NumericIdPrimKeyDto[];
    hasDeadline: boolean;
    deadlineDate: string | null;
    isOfficial: boolean;
    officialReference: string | null;
    notes: string | null;
    taskEvent?: TaskEventDto | null;
    dmsData: DmsDataDto | null;
    replyTo?: string[];
    isAutomatic?: boolean;
}

export interface TaskForm {
    id?: string | number | null;
    name?: string;
    description?: string;
    taskStatusTypeCode?: string;
    taskPriority?: MinStringOption;
    taskTypes?: MinStringOption[];
    assignedToEmployees?: NamedEntityMinDto[];
    assignedByEmployees?: NamedEntityMinDto[];
    clientParties: NamedEntityMinDto[];
    matters: TaskMatterForm[];
    assignments: MatterAssignmentMinDto[];
    correspondence: CorrespondenceListElementMinDto[];
    debriefings: DebriefingMin[];
    hasDeadline?: boolean;
    isOfficial: boolean;
    officialReference: string;
    notes: string;
    calendarSync?: boolean;
    deadlineDate: string | null;
    taskEvent: TaskEventForm | null;
    dmsData: DmsDataForm | null;
    replyTo?: string[];
    isAutomatic?: boolean;
}

export interface TaskEventForm {
    id?: string | number | null;
    inviteSender?: string | null;
    description: string | null;
    eventLocation: string | null;
    reminder: ReminderType | null;
    dateStart: string | null;
    timeStart: Date | null;
    dateEnd: string | null;
    timeEnd: Date | null;
    eventInvitees: NamedEntityMinDto[] | null;
    allDayMeeting: boolean;
    eventId?: string | null;
}

export interface TaskEventDto {
    id?: string | number | null;
    inviteSender: string | null;
    description: string | null;
    eventLocation: string | null;
    reminder: string | null;
    dateStart: Date | null;
    dateEnd: Date | null;
    eventInvitees: EventInviteeDto[] | null;
    isAllDayEvent: boolean;
    eventId?: string | null;
}

interface DmsDataForm {
    dmsLink: string | null;
    dmsDatabase?: MinStringOption | null;
    dmsNumber?: string | null;
}

interface DmsDataDto {
    dmsLink: string | null;
    dmsDatabase?: string | null;
    dmsNumber?: string | null;
}

interface EventInviteeDto {
    employeeId: number;
    employeeName?: string | null;
}

interface ReminderType {
    label: string;
    value: string;
}

/**
 * Where an automatic task is worked on. The automatic tasks are the AML form follow-ups the backend derives
 * from a client's compliance checks and have no task row of their own, so they open that client's Compliance
 * tab. One without a client has nowhere to open.
 */
export const getAutomaticTaskUrl = (task: { clientParties?: { id: number }[] | null }): string | undefined => {
    const partyId = task.clientParties?.[0]?.id;
    return partyId ? `/contact/${partyId}/compliance` : undefined;
};

/** Where a task opens: its own page, or for an automatic task, which has no task row behind it, where it is done. */
export const getTaskUrl = (task: Pick<TaskListItem, 'id' | 'isAutomatic' | 'clientParties'>): string | undefined =>
    task.isAutomatic ? getAutomaticTaskUrl(task) : `/task/${task.id}`;

export const getPriorityStatusChipType = (taskPriorityTypeCode: string) => {
    switch (taskPriorityTypeCode) {
        case 'HIGH':
            return 'error';
        case 'MEDIUM':
            return 'warning';
        case 'LOW':
            return 'neutral';
        default:
            return 'success';
    }
};

export interface TaskMatterForm extends MatterVisibilityInfo {
    id: number;
    name: string;
    fullName: string;
    caseNumber: string;
    clientParty: ClientParty;
    description: string;
    employees: {
        employee: NamedEntityMinDto;
        matterEmployeeRoleTypeCode: string;
    }[];
    responsibleEmployee?: NamedEntityMinDto;
    supervisedByEmployee?: NamedEntityMinDto;
}

export interface OrganizerEmailDto {
    organizerEmail: string;
}
